import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Bot, MessageSquare } from "lucide-react";
import { ChatFlow, Question } from "@/types/theme";
import { AIChatModeSettings } from "./AIChatModeSettings";
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  defaultDropAnimationSideEffects,
  DropAnimation
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { SortableQuestionItem } from "./SortableQuestionItem";
import { useState, useEffect, useRef, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";

interface BuilderFlowProps {
  chatFlow: ChatFlow;
  onUpdateChatFlow: (chatFlow: Partial<Pick<ChatFlow, 'welcomeMessage' | 'completionMessage'>>) => void;
  questionTypes: ReadonlyArray<{ readonly value: string; readonly label: string; }>;
  onAddQuestion: () => void | Promise<void>;
  onUpdateQuestion: (questionId: string, updates: Partial<Question>) => void | Promise<void>;
  onDeleteQuestion: (questionId: string) => void | Promise<void>;
  onReorderQuestions: (reorderedQuestions: Question[]) => void | Promise<void>;
  onAddOption: (questionId: string) => void;
  onUpdateOption: (questionId: string, optionIndex: number, value: string) => void;
  onDeleteOption: (questionId: string, optionIndex: number) => void;
  reportUnsavedChanges?: (isDirty: boolean) => void;
  chatbotId?: string;
  // Chat mode props
  chatMode?: 'question_flow' | 'ai_only';
  onChatModeChange?: (mode: 'question_flow' | 'ai_only') => void;
}

export const BuilderFlow = ({
  chatFlow,
  onUpdateChatFlow,
  questionTypes,
  onAddQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
  onReorderQuestions,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  reportUnsavedChanges,
  chatbotId,
  chatMode = 'question_flow',
  onChatModeChange,
}: BuilderFlowProps) => {
  const [welcomeMessage, setWelcomeMessage] = useState(chatFlow.welcomeMessage);
  const [completionMessage, setCompletionMessage] = useState(chatFlow.completionMessage);
  const [questions, setQuestions] = useState<Question[]>(chatFlow.questions || []);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showCompletionSuccess, setShowCompletionSuccess] = useState(false);
  const [showWelcomeSuccess, setShowWelcomeSuccess] = useState(false);

  const { toast } = useToast();

  // Track if the component has received initial values and if user is actively dragging
  const initializedRef = useRef(false);
  const isDraggingRef = useRef(false);

  // Update local state from props only on initial load or when props change
  useEffect(() => {
    // Only update if this is the first load or if the values have changed externally
    if (!initializedRef.current ||
        (welcomeMessage === chatFlow.welcomeMessage && completionMessage === chatFlow.completionMessage)) {
      setWelcomeMessage(chatFlow.welcomeMessage);
      setCompletionMessage(chatFlow.completionMessage);
      initializedRef.current = true;
    }

    // Don't reset questions if user is actively dragging or has made local changes
    if (!isDraggingRef.current && !activeId) {
      const newQuestions = chatFlow.questions || [];
      // Only update if questions actually changed
      if (JSON.stringify(questions) !== JSON.stringify(newQuestions)) {
        setQuestions(newQuestions);
      }
    }

    reportUnsavedChanges?.(false);
  }, [chatFlow.welcomeMessage, chatFlow.completionMessage, chatFlow.questions]); // Removed activeId from dependencies

  // Memoize the dirty state calculation to prevent infinite loops
  const isDirty = useMemo(() => {
    return welcomeMessage !== chatFlow.welcomeMessage ||
           completionMessage !== chatFlow.completionMessage ||
           JSON.stringify(questions) !== JSON.stringify(chatFlow.questions || []);
  }, [welcomeMessage, completionMessage, questions, chatFlow.welcomeMessage, chatFlow.completionMessage, chatFlow.questions]);

  useEffect(() => {
    reportUnsavedChanges?.(isDirty);
  }, [isDirty]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 2 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 50, tolerance: 10 },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    isDraggingRef.current = true;
    document.body.classList.add('dragging');
  };

  const handleDragOver = (event: DragOverEvent) => {
    console.log("Dragging over:", event.over?.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    document.body.classList.remove('dragging');
    isDraggingRef.current = false;
    setActiveId(null);

    if (!over) {
      console.log("Drag ended with no target");
      return;
    }

    if (active.id !== over.id) {
      console.log("Drag ended:", active.id, "dropped over:", over.id);

      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        console.log("Reordering from", oldIndex, "to", newIndex);

        const reorderedQuestions = arrayMove(questions, oldIndex, newIndex).map((q, index) => ({ ...q, order: index }));
        setQuestions(reorderedQuestions);
        onReorderQuestions(reorderedQuestions);
        toast({
          title: "Question order updated locally",
          description: "Changes will be saved with the next flow update.",
          duration: 2000,
        });
      } else {
        console.log("Could not find question indices", { oldIndex, newIndex, activeId: active.id, overId: over.id });
      }
    }
  };

  const handleWelcomeMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWelcomeMessage(e.target.value);
  };

  const handleWelcomeMessageBlur = () => {
    // Only update if the value has changed
    if (welcomeMessage !== chatFlow.welcomeMessage) {
      console.log(`Saving welcome message: ${welcomeMessage}`);
      onUpdateChatFlow({ welcomeMessage });

      // Show success toast
      toast({
        title: "Welcome message saved",
        description: "Your changes have been saved successfully.",
        duration: 2000,
      });

      // Show inline success message
      setShowWelcomeSuccess(true);
      setTimeout(() => {
        setShowWelcomeSuccess(false);
      }, 2000);
    }
  };

  const handleCompletionMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCompletionMessage(e.target.value);
  };

  const handleCompletionMessageBlur = () => {
    // Only update if the value has changed
    if (completionMessage !== chatFlow.completionMessage) {
      console.log(`Saving completion message: ${completionMessage}`);
      onUpdateChatFlow({ completionMessage });

      // Show success toast
      toast({
        title: "Completion message saved",
        description: "Your changes have been saved successfully.",
        duration: 2000,
      });

      // Show inline success message
      setShowCompletionSuccess(true);
      setTimeout(() => {
        setShowCompletionSuccess(false);
      }, 2000);
    }
  };

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.currentTarget.blur();
    }
  };

  const activeQuestion = activeId ? questions.find(q => q.id === activeId) : null;

  return (
    <div className="space-y-6">
      {/* Chat Mode Selection - Compact Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MessageSquare className="h-5 w-5" />
              <span>Chat Mode</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm">
                <MessageSquare className="h-4 w-4" />
                <span className={chatMode === 'question_flow' ? 'font-medium' : 'text-muted-foreground'}>
                  Question Flow
                </span>
              </div>
              <Switch
                checked={chatMode === 'ai_only'}
                onCheckedChange={(checked) => {
                  const newMode = checked ? 'ai_only' : 'question_flow';
                  onChatModeChange?.(newMode);
                  // Note: reportUnsavedChanges is handled by onChatModeChange since it does immediate save
                }}
              />
              <div className="flex items-center space-x-2 text-sm">
                <Bot className="h-4 w-4" />
                <span className={chatMode === 'ai_only' ? 'font-medium' : 'text-muted-foreground'}>
                  AI-Only
                </span>
              </div>
            </div>
          </CardTitle>
          <CardDescription>
            {chatMode === 'question_flow'
              ? 'Structured conversation with predefined questions and flow logic. Perfect for lead generation, surveys, and guided interactions.'
              : 'Free-form conversation powered by AI. Users can ask anything and the AI responds naturally based on your configuration.'
            }
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Conditional Content Based on Chat Mode */}
      {chatMode === 'question_flow' ? (
        // Question Flow Content
        <>
      <div className="bg-white rounded-lg p-6 border">
        <h3 className="text-lg font-semibold mb-4">Welcome Message</h3>
        {showWelcomeSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative mb-4">
            <span className="block sm:inline">Welcome message saved successfully!</span>
          </div>
        )}
        <textarea
          value={welcomeMessage}
          onChange={handleWelcomeMessageChange}
          onBlur={handleWelcomeMessageBlur}
          onKeyDown={handleTextKeyDown}
          className="w-full p-3 border rounded-md"
          rows={3}
        />
      </div>

      <div className="bg-white rounded-lg p-6 border">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Questions Flow</h3>
          <Button onClick={onAddQuestion}>
            <Plus className="mr-2 h-4 w-4" />
            Add Message
          </Button>
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={questions.map(q => q.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {questions.length === 0 ? (
                <div className="text-center p-8 border border-dashed rounded-lg bg-gray-50">
                  <p className="text-gray-500">No questions added yet. Click "Add Message" to create your first question.</p>
                </div>
              ) : (
                questions.map((question, index) => (
                  <SortableQuestionItem
                    key={question.id}
                    question={question}
                    index={index}
                    handleUpdateQuestion={onUpdateQuestion}
                    handleDeleteQuestion={onDeleteQuestion}
                    questionTypes={questionTypes}
                    handleAddOption={onAddOption}
                    handleUpdateOption={onUpdateOption}
                    handleDeleteOption={onDeleteOption}
                    chatbotId={chatbotId}
                  />
                ))
              )}
            </div>
          </SortableContext>

          <DragOverlay dropAnimation={dropAnimation}>
            {activeId && activeQuestion ? (
              <div className="bg-white border-2 border-primary rounded-lg shadow-lg p-4 w-full opacity-90">
                <div className="flex items-center">
                  <div className="ml-2 flex-1">
                    <p className="font-medium truncate">{activeQuestion.text}</p>
                    <p className="text-sm text-gray-500">{activeQuestion.type}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

          <div className="bg-white rounded-lg p-6 border">
            <h3 className="text-lg font-semibold mb-4">Completion Message</h3>
            {showCompletionSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative mb-4">
                <span className="block sm:inline">Completion message saved successfully!</span>
              </div>
            )}
            <textarea
              value={completionMessage}
              onChange={handleCompletionMessageChange}
              onBlur={handleCompletionMessageBlur}
              onKeyDown={handleTextKeyDown}
              className="w-full p-3 border rounded-md"
              rows={3}
              placeholder="Enter the message to show after all questions are answered"
            />
          </div>
        </>
      ) : (
        // AI-Only Chat Content
        <AIChatModeSettings />
      )}
    </div>
  );
};
